
import React, { useRef, useState } from "react";
import type { AttachmentMeta } from "../../types/attachments";
import { SUPPORTED_UPLOAD_EXTENSIONS } from "../../constants/fileTypes";

interface FileUploadKitProps {
  label?: string;
  description?: string;
  accept?: string[];
  multiple?: boolean;
  maxSizeMB?: number;
  disabled?: boolean;
  existingFiles?: AttachmentMeta[];
  
  // For displaying files waiting to be uploaded (raw Files)
  rawFiles?: File[];
  onRemoveRawFile?: (index: number) => void;

  // Callback when files are selected/dropped (parent handles upload or temp storage)
  onFilesSelected?: (files: File[]) => void;
  
  // Callback when a remote file is removed
  onRemoveFile?: (file: AttachmentMeta, index: number) => void;
}

export const FileUploadKit: React.FC<FileUploadKitProps> = ({
  label = "上傳檔案",
  description = "拖曳檔案到此，或點擊按鈕選擇",
  accept = SUPPORTED_UPLOAD_EXTENSIONS, // Default to spec-compliant extensions
  multiple = true,
  maxSizeMB = 20,
  disabled = false,
  existingFiles = [],
  rawFiles = [],
  onFilesSelected,
  onRemoveFile,
  onRemoveRawFile,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  // Validate File Extension against global whitelist if using default accept, or provided accept list
  const isExtensionAllowed = (file: File) => {
    const name = file.name.toLowerCase();
    return accept.some(ext => name.endsWith(ext.toLowerCase()));
  };

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || disabled) return;
    
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(fileList).forEach(file => {
      // Size Check
      if (file.size > maxSizeMB * 1024 * 1024) {
        errors.push(`${file.name}: 超過大小限制 (${maxSizeMB}MB)`);
        return;
      }
      
      // Extension Check
      if (!isExtensionAllowed(file)) {
        errors.push(`${file.name}: 格式不支援 (僅支援 ${accept.join(", ")})`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert("部分檔案無法上傳：\n" + errors.join("\n"));
    }

    if (validFiles.length > 0 && onFilesSelected) {
      onFilesSelected(validFiles);
    }
    
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const openFile = (file: AttachmentMeta) => {
    if (file.url) {
      window.open(file.url, "_blank");
    } else {
      alert("預覽無法開啟");
    }
  };

  return (
    <div className="file-upload-kit" style={{ marginTop: 12 }}>
      {/* Drop Zone */}
      <div 
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? "var(--ok)" : "var(--border)"}`,
          borderRadius: 8,
          padding: "20px",
          textAlign: "center",
          background: dragActive ? "rgba(96, 230, 179, 0.1)" : "rgba(255,255,255,0.02)",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.2s",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8
        }}
      >
        <div style={{ fontSize: 24, color: "var(--muted)" }}>☁️</div>
        <div>
            <div style={{ fontWeight: 500, color: "var(--text)", fontSize: 14 }}>{label}</div>
            {description && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{description}</div>}
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                支援格式: {accept.join(" ")}
            </div>
        </div>
        <input 
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept.join(",")}
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled}
        />
      </div>

      {/* Pending Files (Local) */}
      {rawFiles.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{fontSize: 11, color: '#fbbf24'}}>等待上傳 ({rawFiles.length})</div>
          {rawFiles.map((f, idx) => (
            <div key={`raw-${idx}`} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "rgba(255,255,255,0.05)", padding: "8px 12px", borderRadius: 6, border: "1px dashed #666"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
                <span style={{ fontSize: 16 }}>📄</span>
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 13, color: "#fff", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {(f.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              {!disabled && onRemoveRawFile && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemoveRawFile(idx); }}
                  style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 18, padding: "0 4px" }}
                  title="移除"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Existing Files List (Remote) */}
      {existingFiles.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{fontSize: 11, color: 'var(--muted)'}}>已上傳檔案 ({existingFiles.length})</div>
          {existingFiles.map((f, idx) => (
            <div key={idx} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#222", padding: "8px 12px", borderRadius: 6, border: "1px solid #333"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
                <span style={{ fontSize: 16 }}>{f.url ? "☁️" : "📄"}</span>
                <div style={{ overflow: "hidden" }}>
                  <div 
                    onClick={() => openFile(f)}
                    style={{ 
                      fontSize: 13, color: f.url ? "#60a5fa" : "#e5e7eb", 
                      whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
                      cursor: f.url ? "pointer" : "default", textDecoration: f.url ? "underline" : "none"
                    }}
                    title={f.name}
                  >
                    {f.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    {f.size ? (f.size / 1024).toFixed(1) + " KB" : "Unknown size"}
                  </div>
                </div>
              </div>
              
              {!disabled && onRemoveFile && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemoveFile(f, idx); }}
                  style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 18, padding: "0 4px" }}
                  title="移除檔案"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
